import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCreateEstablishmentInfrastructure } from '@/features/maintenance/establishmentInfrastructures/hooks/useCreateEstablishmentInfrastructure'
import {
  establishmentInfrastructureCreateSchema,
  EstablishmentInfrastructureValidationSchema,
} from '@/features/maintenance/establishmentInfrastructures/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { QueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

const Create = () => {
  const goBack = useGoBack()
  const navigate = useNavigate()
  const queryClient = new QueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToCreateEstablishmentInfrastructure =
    isUserPermissionsPending || hasUserPermission('Admin::Maintenance::EstablishmentInfrastructures::Create')

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EstablishmentInfrastructureValidationSchema>({
    resolver: zodResolver(establishmentInfrastructureCreateSchema),
  })

  const { mutate: createEstablishmentInfrastructure, isPending: createEstablishmentInfrastructureIsPending } =
    useCreateEstablishmentInfrastructure({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['establishmentInfrastructures'] })
        navigate('/establishment-infrastructures')
      },
      onError: FormTransformers.errorsTransformer({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<EstablishmentInfrastructureValidationSchema> = (data) =>
    createEstablishmentInfrastructure(data)

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={userHasPermissionToCreateEstablishmentInfrastructure ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false" className="flex flex-col gap-y-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/establishment-infrastructures" />

            <p className="text-white-400 font-normal">Infraestrutura da Rede Credenciada</p>

            <ChevronsRightIcon className="text-white-400" />

            <h2>Nova Infraestrutura</h2>
          </div>

          <div className="flex gap-4">
            <Button variant="outline-error" onClick={() => goBack({ fallback: '/establishment-infrastructures' })}>
              Cancelar
            </Button>

            <Button
              type="submit"
              name="create_establishment_infrastructure"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={createEstablishmentInfrastructureIsPending}
              isLoading={createEstablishmentInfrastructureIsPending}
            >
              Criar Infraestrutura
            </Button>
          </div>
        </div>

        <Box className="max-h-[calc(100vh-128px)] flex flex-col gap-4 overflow-y-auto">
          <p className="text-lg font-bold">Infraestrutura da Rede Credenciada</p>

          <TextField
            label="Nome da infraestrutura"
            markAsRequired
            {...register('display_name')}
            errorMessage={errors?.display_name?.message}
            disabled={createEstablishmentInfrastructureIsPending}
          />
        </Box>

        {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
